import React, { useState, useEffect, useRef } from "react";
import {
  Combobox,
  ComboboxOptions,
  ComboboxOption,
  ComboboxInput,
} from "@headlessui/react";
import useMatomoEvent from "./hooks/useMatomoEvent.js";
import {
  ArrowLeftStartOnRectangleIcon,
  EnvelopeOpenIcon,
} from "@heroicons/react/24/solid";

const PrivacyCheckbox = ({ checked, onChange }) => {
  return (
    <div className="flex items-center gap-3">
      {/* Estilizado del checkbox */}
      <div className="group grid size-5 grid-cols-1">
        <input
          id="privacy"
          name="privacy"
          type="checkbox"
          checked={checked}
          onChange={onChange}
          required
          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-blue-500 checked:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100"
        />
        {/* Icono de check (aparece solo cuando está checked) */}
        <svg
          fill="none"
          viewBox="0 0 14 14"
          className="pointer-events-none col-start-1 row-start-1 size-4 self-center justify-self-center stroke-white group-has-[:checked]:opacity-100 opacity-0"
        >
          <path
            d="M3 8L6 11L11 3.5"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Label con el enlace a la política de privacidad */}
      <label htmlFor="privacy" className="text-sm text-gray-900">
        Acepto la{" "}
        <a
          href="/politica-de-privacidad"
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-blue-500 hover:underline"
        >
          Política de Privacidad
        </a>
        .
      </label>
    </div>
  );
};

export function ContactForm({ onClose, municipioInicial = "" }) {
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const trackEvent = useMatomoEvent();
  const [submitted, setSubmitted] = useState(false);
  const [setError] = useState(null);
  const divRef = useRef(null);

  const [municipios, setMunicipios] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedMunicipio, setSelectedMunicipio] = useState(
    municipioInicial || ""
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (submitted && divRef.current) {
      setTimeout(() => {
        divRef.current.classList.remove("opacity-0", "translate-y-1/2");
      }, 10);
    }
  }, [submitted]);

  useEffect(() => {
    fetch(
      "https://datos.comunidad.madrid/api/3/action/datastore_search?resource_id=48010e29-caa0-46c5-ab6e-f498b2dc042f&limit=500"
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.result.records) {
          setMunicipios(data.result.records.map((m) => m.municipio_nombre));
        }
      })
      .catch((err) => console.error("Error al obtener municipios:", err))
      .finally(() => setLoading(false));
  }, []);

  const filteredMunicipios =
    query === ""
      ? municipios
      : municipios.filter((m) => m.toLowerCase().includes(query.toLowerCase()));

  const encode = (data) =>
    Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key])
      )
      .join("&");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);

    formData.append("municipio", selectedMunicipio);
    formData.append("form-name", form.getAttribute("name"));

    const dataObject = Object.fromEntries(formData);
    console.log("Campos del formulario enviados:", dataObject);

    const pagePath = window.location.pathname;

    //Splitbee
    window.splitbee.track("Formulario Enviado", {
      page: pagePath, // Enviar la URL donde se envió el formulario
    });

    //Matomo
    trackEvent("Formulario", "Enviado", `${pagePath}`);

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(dataObject),
      });

      setSubmitted(true);
    } catch (err) {
      console.error("Error al enviar el formulario:", err);
      // Tracking de error en Splitbee
      window.splitbee.track("Formulario Error", {
        page: pagePath, // Enviar la URL donde falló el formulario
      });
      // Tracking de error en Matomo
      trackEvent("Formulario", "Error al enviar", `${pagePath}`);
      setError(
        "Hubo un error al enviar el formulario. Por favor, inténtalo de nuevo."
      );
    }
  };

  if (submitted) {
    return (
      <div
        ref={divRef}
        className="py-6 opacity-0 translate-y-1/2 relative z-10 transition-all duration-300"
      >
        <EnvelopeOpenIcon className=" size-56 fill-slate-100 mb-4 mx-auto" />
        <h2 className="text-2xl font-semibold text-[#3B74D9] mb-2">
          Mensaje Enviado
        </h2>
        <p className="text-sm text-gray-700 mb-8">
          Hemos recibido tu solicitud y nos pondremos en contacto contigo lo
          antes posible.
        </p>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="btn flex items-center justify-center w-full normal-case text-sm gap-1 rounded-md "
            aria-label="Cerrar"
          >
            <ArrowLeftStartOnRectangleIcon className="size-5" /> Cerrar
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      {/* 🔹 Formulario oculto para Netlify */}
      <form name="contacto" data-netlify="true" className="hidden">
        <input type="hidden" name="form-name" value="contacto" />
        <input type="text" name="name" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <input type="text" name="municipio" />
        <textarea name="message"></textarea>
        <input type="checkbox" name="privacy" />
      </form>

      {/* 🔹 Formulario real */}
      <form
        name="contacto"
        method="POST"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={handleSubmit}
        className="space-y-4 text-sm"
      >
        <input type="hidden" name="form-name" value="contacto" />

        <div>
          <label htmlFor="name" className="block">
            Nombre
          </label>
          <input
            type="text"
            name="name"
            id="name"
            required
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          />
        </div>

        <div>
          <label htmlFor="email" className="block">
            Email
          </label>
          <input
            type="email"
            name="email"
            id="email"
            required
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block">
            Teléfono
          </label>
          <input
            type="tel"
            name="phone"
            id="phone"
            required
            pattern="^(?:(?:\+|00)34\s?)?(?:[6-9](?:\s?\d){8})$"
            title="Introduce un número de teléfono válido"
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          />
        </div>

        {/* Campo de Municipio con Búsqueda */}
        <div>
          <label htmlFor="municipio" className="block">
            Municipio
          </label>
          <Combobox value={selectedMunicipio} onChange={setSelectedMunicipio}>
            <div className="relative mt-1">
              <ComboboxInput
                className="w-full border border-gray-300 rounded-md p-2"
                displayValue={(municipio) => municipio || ""}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Selecciona un municipio"
              />
              {loading ? (
                <div className="p-2 text-gray-500">Cargando municipios...</div>
              ) : (
                <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto bg-white border border-gray-300 rounded-md shadow-lg">
                  {filteredMunicipios.map((municipio, index) => (
                    <ComboboxOption
                      key={index}
                      value={municipio}
                      className="cursor-pointer select-none p-2 hover:bg-blue-500 hover:text-white"
                    >
                      {municipio}
                    </ComboboxOption>
                  ))}
                </ComboboxOptions>
              )}
            </div>
          </Combobox>
        </div>

        {/* ✅ Campo restaurado: Mensaje */}
        <div>
          <label htmlFor="message" className="block">
            Mensaje
          </label>
          <textarea
            name="message"
            id="message"
            rows="4"
            required
            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
          ></textarea>
        </div>

        {/* Checkbox de Política de Privacidad */}
        <PrivacyCheckbox
          checked={privacyAccepted}
          onChange={(e) => setPrivacyAccepted(e.target.checked)}
        />

        <button
          type="submit"
          className="w-full bg-[#3B74D9] text-white px-6 py-3 rounded-md"
        >
          Enviar
        </button>
      </form>
    </>
  );
}
